import React from 'react';
import './ParticipantGroupDot.css';
import Tooltip from '../tooltip/Tooltip';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import useRoleSettings from '../../hooks/useRoleSettings';
import {
  colorVar, groupRoleLabel, normalizeColorKey, normalizeParticipantGroup, normalizeRole,
  resolveGroupAndRole, roleColorKey,
} from '../../util/participantGroups';

/**
 * The person dot: one dot whose color comes from the group
 * configuration (ColorKey); a role overrides the group color with its own color (configured in /groups,
 * by default Orga red, Admin orange). Groups with the color "neutral" (e.g. guest) show an empty ring.
 *
 * Pass either a row (`row`: participant, player, candidate — resolved via resolveGroupAndRole)
 * or `participantGroup` (+ optional `role`) directly.
 */
function ParticipantGroupDot({ row, participantGroup, role, size = 'sm', className = '', withTooltip = true }) {
  const { byId } = useParticipantGroups();
  // Re-render once the configured role colors/names are loaded (read through roleColorKey/groupRoleLabel).
  useRoleSettings();

  let group;
  let resolvedRole;
  if (row) {
    ({ group, role: resolvedRole } = resolveGroupAndRole(row, byId));
  } else {
    group = typeof participantGroup === 'number'
      ? byId.get(participantGroup) ?? null
      : normalizeParticipantGroup(participantGroup);
    resolvedRole = normalizeRole(role);
  }

  const colorKey = resolvedRole ? normalizeColorKey(roleColorKey(resolvedRole)) : normalizeColorKey(group?.colorKey);
  const filled = colorKey !== 'neutral';
  const label = groupRoleLabel(group, resolvedRole);

  const dot = (
    <div className="participantGroupDotContainer">
      <div
        className={`participantGroupDot ${size}${filled ? ' isFilled' : ''} ${className}`}
        style={filled ? { '--participant-group-dot-color': colorVar(colorKey) } : undefined}
        role="img"
        aria-label={label || undefined}
      />
    </div>
  );

  return withTooltip && label ? <Tooltip text={label}>{dot}</Tooltip> : dot;
}

export default ParticipantGroupDot;
